import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {CardModel} from '../../../models/api/cardModel';
import {AccountDetailsSectionsExpandState} from '../../../models/api/localPreferencesModel';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {AccountDetailsContext} from '../../accounts/accountDetails/accountDetailsContext';
import {
  buildAccountDetailsConversationSection,
  OnAccountDetailsSectionToggledHandler,
} from '../../accounts/accountDetails/helpers/accordianSectionHelpers';
import {Accordion} from '../../common/accordion/accordion';
import {AccountAvatar} from '../../common/avatars/accountAvatar';
import {ContactDetailsTrackingDisplayContextOptionsEnum} from '../contactDetailsTrackingHelpers';
import {contactDetailsAccountDetailsTrackingHandler} from '../helpers/contactDetailsAccountDetailsTracking';

/*
 * Props.
 */

interface ContactDetailsNoAccountCustomDomainProps {
  panelId: ContactDetailsPanelId;
  customDomain: string;
  sectionExpandState: AccountDetailsSectionsExpandState;
  card?: CardModel;

  // Conversations
  conversationStatus: AsyncStatusesEnum | undefined;
  conversationIds: ReadonlyArray<number> | undefined;
  hasMoreConversations: boolean | undefined;

  createNewAccount: Handler;
  onAccountDetailsSectionToggled: OnAccountDetailsSectionToggledHandler;
}

/*
 * Style.
 */

const StyledNoAccountHeaderContainerDiv = styled.div`
  display: flex;
  padding: 20px 16px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledNoAccountDetailsDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  width: 100%;
  margin-left: 12px;
`;

const StyledAccountNameDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 1.4;

  ${ellipsis()};
`;

const StyledAccountActionContainerDiv = styled.div`
  display: flex;
  width: 100%;
`;

const StyledNewAccountButton = styled(Button)`
  margin-left: -12px;
  margin-top: -4px;
`;

const StyledAccountButtonContentDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const ContactDetailsNoAccountCustomDomain: FC<ContactDetailsNoAccountCustomDomainProps> = (props) => {
  const {
    panelId,
    customDomain,
    sectionExpandState,
    conversationStatus,
    conversationIds,
    hasMoreConversations,
    card,
    createNewAccount,
    onAccountDetailsSectionToggled,
  } = props;
  return (
    <>
      <StyledNoAccountHeaderContainerDiv>
        <AccountAvatar size={VisualSizesEnum.EXTRA_LARGE} avatarIconSize={22} />
        <StyledNoAccountDetailsDiv>
          <StyledAccountNameDiv>{customDomain}</StyledAccountNameDiv>
          <StyledAccountActionContainerDiv>
            <StyledNewAccountButton
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={createNewAccount}
            >
              <StyledAccountButtonContentDiv>
                <Trans id="contact-details-no-account-create-account-button">+ Create account</Trans>
              </StyledAccountButtonContentDiv>
            </StyledNewAccountButton>
          </StyledAccountActionContainerDiv>
        </StyledNoAccountDetailsDiv>
      </StyledNoAccountHeaderContainerDiv>
      <AccountDetailsContext.Provider
        value={{
          panelId,
          onTrackEvent: (event) =>
            contactDetailsAccountDetailsTrackingHandler(
              card,
              event,
              ContactDetailsTrackingDisplayContextOptionsEnum.DOMAIN,
            ),
        }}
      >
        <Accordion
          key="standard-accordion"
          sections={[
            buildAccountDetailsConversationSection({
              panelId,
              expandState: sectionExpandState,
              conversationStatus,
              conversationIds,
              hasMoreConversations,
              domain: customDomain,
              onAccountDetailsSectionToggled,
            }),
          ]}
        />
      </AccountDetailsContext.Provider>
    </>
  );
};
